import { Artwork, ArtworkWrapper } from "../../types/artworksData";
import { getProxiedImageUrl } from "../../utils/imageProxy";
import type { ModelLoadSource } from "./utils/general.utils";
import type { ViewerVRRoute, VRViewerKind, VRViewerMedia } from "./renderer/managers/vr.types";
import { resolveArtworkWrapperTransform } from "./utils/artworkPlacement.utils";
import type { RuntimeMediaDelivery } from "../../utils/runtimeConfig";
import { resolveRuntimeMediaUrl } from "../../utils/runtimeMedia";

export interface ArtworkViewerMediaClient {
  getOriginalImage: (artworkId: string) => Promise<any>;
}

export const getArtworkViewerKind = (artwork?: Artwork | null): VRViewerKind => {
  if (!artwork) return null;
  if (artwork.type === "image" || artwork.type === "video" || artwork.type === "3dmodel") {
    return artwork.type;
  }
  return null;
};

const toProxiedImage = (
  url: string | undefined,
  baseAPI: string,
  trustedOrigins: readonly string[] = [],
) => {
  if (!url) return "";
  return getProxiedImageUrl(url, baseAPI || "", trustedOrigins);
};

const isUnsupportedExternalImageUrl = (value?: string) => {
  if (!value) return true;
  const lower = value.toLowerCase();
  return (
    lower.includes("/embed") ||
    lower.includes("/player") ||
    lower.includes("/watch") ||
    lower.includes("/alacarta/") ||
    lower.endsWith(".html") ||
    lower.endsWith(".htm")
  );
};

const resolveModelSource = (artwork: Artwork): ModelLoadSource | null => {
  if (artwork.artworkData.modelSource) {
    return artwork.artworkData.modelSource;
  }
  if (artwork.artworkData.dataUrl) {
    return artwork.artworkData.dataUrl;
  }
  return null;
};

const positiveFiniteNumber = (value: unknown): number | null => {
  const candidate = Number(value);
  return Number.isFinite(candidate) && candidate > 0 ? candidate : null;
};

const resolveModelDisplayHeight = (
  artwork: Artwork,
  artworkWrapper?: Partial<ArtworkWrapper> | null,
  spaceData?: any
): number | undefined => {
  const baseHeight = positiveFiniteNumber(artwork.artworkData?.height);
  if (!baseHeight) return undefined;

  const placement = resolveArtworkWrapperTransform(artworkWrapper, spaceData);
  const scale = placement.transform?.scale;
  const scaleFactor =
    positiveFiniteNumber(scale?.y) ??
    positiveFiniteNumber(scale?.x) ??
    positiveFiniteNumber(scale?.z) ??
    1;

  return baseHeight * 0.01 * scaleFactor;
};

export async function resolveArtworkViewerMedia(
  artwork: Artwork,
  route: ViewerVRRoute,
  options: {
    apiClient?: ArtworkViewerMediaClient;
    baseAPI?: string;
    artworkWrapper?: Partial<ArtworkWrapper> | null;
    spaceData?: any;
    mediaDelivery?: RuntimeMediaDelivery | null;
  } = {}
): Promise<VRViewerMedia | null> {
  const kind = getArtworkViewerKind(artwork);
  if (!kind) {
    return null;
  }

  const baseAPI = options.baseAPI || "";

  if (kind === "image") {
    const sourceImages = Array.isArray(artwork?.source?.images)
      ? artwork.source.images
        .filter((imageUrl) => Boolean(imageUrl) && !isUnsupportedExternalImageUrl(imageUrl))
        .map((imageUrl) => toProxiedImage(imageUrl, baseAPI))
      : [];

    let primaryImageUrl = sourceImages[0] || "";
    if (!primaryImageUrl && artwork.source && artwork.artworkData?.dataOriginalUrl) {
      if (isUnsupportedExternalImageUrl(artwork.artworkData.dataOriginalUrl)) {
        return null;
      }
      primaryImageUrl = toProxiedImage(artwork.artworkData.dataOriginalUrl, baseAPI);
    }

    if (!primaryImageUrl && options.apiClient?.getOriginalImage && artwork._id) {
      const originalImageResponse = await options.apiClient.getOriginalImage(artwork._id);
      if (typeof originalImageResponse === "string") {
        primaryImageUrl = toProxiedImage(originalImageResponse, baseAPI);
      } else if (typeof originalImageResponse?.url === "string") {
        primaryImageUrl = toProxiedImage(originalImageResponse.url, baseAPI);
      } else if (typeof originalImageResponse?.data === "string") {
        primaryImageUrl = toProxiedImage(originalImageResponse.data, baseAPI);
      }
    }

    if (!primaryImageUrl && artwork.artworkData?.dataUrl) {
      if (artwork.source && isUnsupportedExternalImageUrl(artwork.artworkData.dataUrl)) {
        return null;
      }
      primaryImageUrl = toProxiedImage(
        resolveRuntimeMediaUrl(
          artwork.artworkData.dataUrl,
          "image",
          options.mediaDelivery,
          artwork.artworkData.runtimeMedia,
        ) || undefined,
        baseAPI,
        options.mediaDelivery?.cdnDomain ? [options.mediaDelivery.cdnDomain] : [],
      );
    }

    const imageUrls = sourceImages.length > 0
      ? sourceImages
      : primaryImageUrl
        ? [primaryImageUrl]
        : [];

    if (imageUrls.length === 0) {
      return null;
    }

    return {
      kind,
      route,
      artwork,
      artworkId: artwork._id,
      title: artwork.title,
      imageUrl: imageUrls[0],
      imageUrls,
    };
  }

  if (kind === "video") {
    const videoUrl = resolveRuntimeMediaUrl(
      artwork.artworkData?.dataUrl,
      "video",
      options.mediaDelivery,
      artwork.artworkData?.runtimeMedia,
    ) || "";
    if (!videoUrl) return null;
    return {
      kind,
      route,
      artwork,
      artworkId: artwork._id,
      title: artwork.title,
      videoUrl,
    };
  }

  const canonicalModelSource = resolveModelSource(artwork);
  const modelSource = typeof canonicalModelSource === "string"
    ? resolveRuntimeMediaUrl(
      canonicalModelSource,
      "model3d",
      options.mediaDelivery,
      artwork.artworkData?.runtimeMedia,
    )
    : canonicalModelSource;
  if (!modelSource) {
    return null;
  }

  return {
    kind,
    route,
    artwork,
    artworkId: artwork._id,
    title: artwork.title,
    modelSource,
    modelDisplayHeight: resolveModelDisplayHeight(artwork, options.artworkWrapper, options.spaceData),
  };
}
